import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import EqualizerRoundedIcon from "@mui/icons-material/EqualizerRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function EQLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/equalization";
  const pageTitle = "Equalization in Audio: What It Is and How It Works";
  const pageDescription =
    "Learn what EQ is, how equalization works, the main filter types, common EQ ranges, and practical EQ techniques for mixing, mastering, and live sound.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "equalization",
          "EQ",
          "audio EQ",
          "mixing EQ",
          "parametric EQ",
          "graphic equalizer",
          "high pass filter",
          "low pass filter",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Equalization" },
          { "@type": "Thing", name: "Audio frequency spectrum" },
          { "@type": "Thing", name: "Parametric EQ" },
          { "@type": "Thing", name: "Graphic EQ" },
          { "@type": "Thing", name: "Mixing" },
          { "@type": "Thing", name: "Mastering" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Equalization",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "EQ boosts or cuts selected frequency ranges so you can shape tone, improve clarity, and solve mix problems.",
      icon: <EqualizerRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "It helps sounds fit together, reduces muddiness, adds presence, and removes unwanted rumble, hiss, and harshness.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Tracking, mixing, mastering, live sound, feedback control, restoration, and sound design.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const filterTypes = [
    {
      name: "High-Pass Filter",
      description:
        "Removes frequencies below a chosen point. Useful for cleaning up rumble and unnecessary low-end energy.",
    },
    {
      name: "Low-Pass Filter",
      description:
        "Removes frequencies above a chosen point. Useful for taming hiss, harshness, or creating darker tones.",
    },
    {
      name: "Low Shelf",
      description:
        "Boosts or cuts everything below a certain frequency by a broad amount.",
    },
    {
      name: "High Shelf",
      description:
        "Boosts or cuts everything above a certain frequency by a broad amount.",
    },
    {
      name: "Bell / Peak Filter",
      description:
        "Boosts or cuts around a center frequency, with adjustable width. One of the most common shapes in mixing EQ.",
    },
    {
      name: "Notch Filter",
      description:
        "A very narrow cut used for surgical removal of feedback, hum, or resonant problem frequencies.",
    },
  ];

  const eqParameters = [
    {
      name: "Frequency",
      description: "The center point or cutoff point being adjusted.",
    },
    {
      name: "Gain",
      description:
        "The amount of boost or cut applied to the selected frequency area.",
    },
    {
      name: "Q",
      description:
        "The width of the band being affected. Higher Q values are narrower and more surgical; lower Q values are broader and more natural.",
    },
  ];

  const eqTypes = [
    {
      name: "Graphic EQ",
      description:
        "Uses fixed frequency bands with sliders. Common in live sound, room tuning, and broad tonal shaping.",
    },
    {
      name: "Parametric EQ",
      description:
        "Lets you control frequency, gain, and Q for each band. This is one of the most flexible and widely used EQ types.",
    },
    {
      name: "Semi-Parametric EQ",
      description:
        "Usually offers adjustable frequency and gain but less control over Q.",
    },
    {
      name: "Shelving EQ",
      description:
        "Raises or lowers the lows or highs broadly, similar to bass and treble controls on many devices.",
    },
    {
      name: "Dynamic EQ",
      description:
        "Changes EQ amount depending on the signal level, useful when a problem appears only occasionally.",
    },
  ];

  const frequencyRanges = [
    {
      name: "Sub Bass (20–60 Hz)",
      description: "Deep weight and physical power. Too much can overwhelm a mix quickly.",
    },
    {
      name: "Bass (60–250 Hz)",
      description: "Warmth, fullness, and the foundation of kick and bass instruments.",
    },
    {
      name: "Low Mids (250–500 Hz)",
      description: "Body and thickness, but also a common area for muddiness.",
    },
    {
      name: "Mids (500 Hz–2 kHz)",
      description: "Core musical detail and intelligibility for many instruments.",
    },
    {
      name: "High Mids (2–4 kHz)",
      description: "Presence, attack, and clarity. Too much can sound harsh or fatiguing.",
    },
    {
      name: "Presence (4–7 kHz)",
      description: "Definition, articulation, and bite.",
    },
    {
      name: "Brilliance / Air (7–20 kHz)",
      description: "Sparkle, sheen, and openness. Too much can add hiss or brittleness.",
    },
  ];

  const practicalTips = [
    "EQ in context, not just in solo, because a sound that seems thin alone may fit perfectly in the full mix.",
    "Cut before you boost when possible. Removing a problem is often cleaner than trying to add what is missing.",
    "Use high-pass filters on non-bass elements to clear room for kick and bass.",
    "Sweep with a narrow boost to locate ugly resonances, then cut them instead of leaving the boost in place.",
    "Be cautious with too many drastic moves, especially across many tracks, because heavy EQ can make a mix feel unnatural.",
    "Use automation or dynamic EQ when a problem happens only in certain moments.",
  ];

  const applications = [
    "Tracking: removing rumble, shaping source tone, and controlling overly bright or boomy recordings.",
    "Mixing: carving space so instruments do not mask each other.",
    "Mastering: making broad tonal adjustments to the full stereo mix.",
    "Live Sound: flattening room response and reducing feedback.",
    "Repair and Restoration: removing hum, resonances, hiss, or harsh peaks.",
  ];

  const commonPitfalls = [
    "Over-EQing and making sounds harsh, thin, or unnatural.",
    "Boosting frequencies just because a chart says so instead of listening to the actual source.",
    "Using presets without considering the context of the mix.",
    "Trying to boost qualities that are not really present in the recording.",
    "Ignoring how one EQ move affects other elements in the song.",
  ];

  const relatedLinks = [
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Learn how EQ and compression work together to shape tone, consistency, and mix placement.",
    },
    {
      title: "Distortion",
      to: "/learning/distortion",
      description:
        "See how distortion changes harmonic content and why EQ is often essential before and after gain stages.",
    },
    {
      title: "De-Esser",
      to: "/learning/de-esser",
      description:
        "Explore how de-essing acts like frequency-conscious dynamic control for harsh vocal sibilance.",
    },
    {
      title: "Mastering Basics",
      to: "/learning/mastering-basics",
      description:
        "Understand how broad EQ decisions influence the overall balance and polish of a finished mix.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Equalization (EQ) | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Equalization
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Equalization, usually called EQ, is the process of adjusting the
                      balance of frequencies within an audio signal. It is one of the
                      most important tools in mixing, mastering, live sound, and sound
                      design because it lets you shape tone with precision.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      EQ can be corrective, creative, subtle, or dramatic, depending on
                      what the material needs. It can remove distractions, reveal detail,
                      and help every sound find its place in the mix.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GraphicEqRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What EQ Does
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Every sound contains a range of frequencies. EQ gives you
                    control over those frequencies by letting you boost or cut
                    specific areas of the spectrum. In simple terms, EQ is
                    frequency-specific volume control.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    You might use EQ to remove low-end rumble from a vocal, soften
                    harsh cymbals, add presence to a guitar, reduce muddiness in a
                    piano, or help a bass and kick drum coexist. Sometimes the goal
                    is transparency and problem-solving. Other times the goal is
                    obvious tone shaping.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      A Brief History of EQ
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    EQ has roots in early telephony, where engineers needed ways
                    to compensate for frequency loss over long transmission lines.
                    The original goal of equalization was to make the signal more
                    even across the spectrum.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    As professional audio developed, EQ became a creative and
                    corrective tool in cinema, broadcasting, recording studios, and
                    live sound systems. Over time, graphic EQs, parametric EQs, and
                    digital plugin EQs made tonal control more precise and more
                    accessible. Today, EQ is built into nearly every mixing
                    workflow.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                      >
                        How EQ Works
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      EQ works through filters. A filter changes how much of a
                      certain frequency range passes through the system.
                    </Typography>

                    <Stack spacing={1.2}>
                      {filterTypes.map((filter) => (
                        <Paper
                          key={filter.name}
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {filter.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {filter.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                      >
                        EQ Parameters
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {eqParameters.map((param) => (
                        <Paper
                          key={param.name}
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {param.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {param.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EqualizerRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Types of Equalizers
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {eqTypes.map((type) => (
                        <Paper
                          key={type.name}
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {type.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {type.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GraphicEqRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        EQ by Frequency Range
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {frequencyRanges.map((range) => (
                        <Paper
                          key={range.name}
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {range.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {range.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LibraryMusicRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Applications in Production and Live Sound
                    </Typography>
                  </Stack>

                  <Box
                    component="ul"
                    sx={{
                      pl: 2.5,
                      m: 0,
                      color: style.color3,
                      "& li": { mb: 1.15, lineHeight: 1.75 },
                    }}
                  >
                    {applications.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </Box>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    One especially useful mixing approach is complementary or
                    mirrored EQ. For example, if two instruments are competing in
                    similar ranges, cutting a little in one while boosting a little
                    in the other can help them both remain audible without simply
                    turning them up.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Practical Tips
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.15, lineHeight: 1.75 },
                      }}
                    >
                      {practicalTips.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarningAmberRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Common Pitfalls
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.15, lineHeight: 1.75 },
                      }}
                    >
                      {commonPitfalls.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Final Thoughts
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    EQ is both technical and artistic. It can remove distractions,
                    reveal detail, and help every sound find its place, but it also
                    shapes the emotional impact of a track. A small cut or boost can
                    change how open, warm, aggressive, or intimate something feels.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    The best EQ decisions come from listening closely and making
                    moves that serve the song. Used with intention, EQ is one of the
                    most powerful tools in audio production.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Related Learning Topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                            width: "100%",
                            minHeight: "75%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}
                            >
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              component={RouterLink}
                              to={item.to}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: `1px solid ${accent}`,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * We are not affiliated with any brands or artists mentioned on this site. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}


// import {React, useState, useContext, useEffect, useRef} from "react"
// import {useStyle} from '../../context/styleContext/styleContext'
// import ReactGA from 'react-ga4';
// import { Paper } from '@mui/material';
// import { Container, Typography, Divider, Box, List, ListItem, ListItemText } from "@mui/material";
// import WikiCommonsImage from "../../components/WikiCommonsImage/WikiCommonsImage";

// export default function EQLearning(){

//   const { style, setStyle } = useStyle();
//   const [width, setWidth] = useState(window.innerWidth)

// function handleWindowSizeChange() {
//     setWidth(window.innerWidth);
// }
// useEffect(() => {
//     window.addEventListener('resize', handleWindowSizeChange);
//     return () => {
//         window.removeEventListener('resize', handleWindowSizeChange);
//     }
// }, []);

// const isMobile = width <= 768;


// ReactGA.send({
//     hitType:"pageview",
//     page:"learning/Equalization",
//     title:"learning/Equalization",
//   })

//   const [height, setHeight] = useState(window.innerHeight)

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

// const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);




//     return(
//     <><div style={{margin:'auto', width:'95%', maxWidth:'800px'}}>

//     <h1 style={{marginTop:'25px'}} ref={topContainer} >Equalization</h1>
//     <Divider className="mobile-card-divider" />
//         <p>Equalization, commonly referred to as EQ, is the process of adjusting the balance of frequency components within an audio signal. It is commonly used to enhance sonic clarity, fix problem areas in a mix, or creatively shape a sound. EQ is one of the most essential tools in both live sound and studio production. Every sound we hear is made up of a range of frequencies, and EQ gives us the power to boost or cut specific ranges to mold that sound into something more pleasing, functional, or fitting in a mix.</p>
//         <p>At its core, EQ allows for selective control of frequency bands. This means that different frequencies in your music can be boosted or cut, changing the sound. This can be done with specific frequencies, or wider bands, such as "low", "mid", and "high". These changes can be subtle or drastic, depending on the goal. From eliminating unwanted rumble and hiss to emphasizing the attack of a snare drum or softening the harshness of a vocal, EQ functions as a frequency-specific volume control.</p>
//         <div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'400px', textAlign:'left'}}>

//         <WikiCommonsImage width="100%" bottomText="Yamaha EQ-500 Graphic Equalizer" bottomTextLinkUrl="https://www.yamaha.com/" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Yamaha_EQ-500_Graphic_Equalizer.jpg" imageSource="https://upload.wikimedia.org/wikipedia/commons/f/fa/Yamaha_EQ-500_Graphic_Equalizer.jpg" alt = "Yamaha EQ-500 Graphic Equalizer"  title = "Touho_T, CC BY 2.0 <https://creativecommons.org/licenses/by/2.0>, via Wikimedia Commons" />
// </div>
//         <h2 style={{marginTop:'30px'}}>A Brief History of EQ</h2>
//         <p>The roots of EQ go back to the early days of telephony, when engineers needed to compensate for frequency loss over long-distance lines. This was done with passive filters that adjusted the frequency response. The term "equalization" referred to the goal of making the signal equally strong across all frequencies—a flat response.</p>
//         <p>As audio equipment evolved, so did the application of EQ. In the 1920s, John Volkman of <a href="https://www.rca.com/" target="_blank" rel="noopener noreferrer">RCA</a> developed variable EQ for use in cinema sound systems. By the 1950s and 60s, pioneers like Art Davis and Daniel Flickinger were designing more sophisticated systems, including the first parametric equalizers, which allowed for adjustable frequency selection, gain, and bandwidth.</p>
//         <p>The 1970s saw the rise of graphic equalizers, with their now-familiar rows of vertical sliders. These were commonly used in studios and live sound reinforcement, enabling engineers to visually sculpt sound across multiple frequency bands. In the digital era, parametric EQs have become more precise, accessible, and flexible than ever—especially through DAW plugins.</p>
//         <h2 style={{marginTop:'30px'}}>How EQ Works</h2>
//         <p>o understand how EQ works, it's helpful to think in terms of filters. A filter is a circuit or algorithm that boosts or attenuates frequencies. The main types of EQ filters include:</p>
//         <ul class="a">
//             <li><strong>High-pass filter (Low-cut): </strong>Removes low frequencies below a set threshold.</li>
//             <li><strong>Low-pass filter (High-cut): </strong>Removes high frequencies above a set threshold.</li>
//             <li><strong>Shelving filters: </strong>Boost or cut frequencies above (high shelf) or below (low shelf) a certain point.</li>
//             <li><strong>Bell (peak/dip) filters: </strong>Boost or cut around a center frequency, with a controllable bandwidth.</li>
//         </ul>
//         <h2 style={{marginTop:'30px'}}>EQ Parameters</h2>
//         <p>Modern EQ units—whether analog or digital—typically offer several adjustable parameters:</p>
//         <ul class="a">
//             <li><strong>Frequency: </strong>The center frequency of the range being adjusted.</li>
//             <li><strong>Gain: </strong>The amount of boost or cut applied to the selected frequency range.</li>
//             <li><strong>Q (Quality factor): </strong>Determines the width of the affected frequency band. Higher Q values affect narrower ranges.</li>
//         </ul>
//         <p>Understanding these controls allows engineers to perform precise adjustments, which is especially critical in complex mixes.</p>
//         <h2 style={{marginTop:'30px'}}>Types of Equalizers</h2>
//         <h3>Graphic Equalizer</h3>
//         <p>Graphic EQs feature a fixed set of frequency bands, each with a dedicated slider. Moving a slider boosts or cuts that band, and the position of all the sliders together forms a visual "graph" of the EQ curve. Common configurations include 15-band (2/3 octave) and 31-band (1/3 octave) models, often used in live sound and venue tuning.</p>
//         <h3>Parametric Equalizer</h3>
//         <p>Parametric EQs allow complete control over each band: the center frequency, the Q factor, and the gain. These are the most flexible type of EQ and are widely used in mixing and mastering. Semi-parametric EQs offer adjustable frequency and gain but fixed Q.</p>
//         <h3>Shelving Equalizer</h3>
//         <p>Shelving EQs adjust frequencies above or below a certain point by a fixed amount. These are commonly seen in consumer audio gear as "bass" and "treble" controls.</p>
//         <h2 style={{marginTop:'30px'}}>EQ by Frequency Range</h2>
//         <p>EQ decisions often correspond to general frequency bands:</p>
//         <ul class="a">
//             <li><strong>Sub Bass (20–60 Hz): </strong>Adds physical power, but too much can overwhelm.</li>
//             <li><strong>Bass (60–250 Hz):  </strong>The foundation of the mix; warmth and fullness.</li>
//             <li><strong>Low Mids (250–500 Hz): </strong>Can add body or muddiness.</li>
//             <li><strong>Mids (500 Hz–2 kHz): </strong>Where much of the musical detail resides.</li>
//             <li><strong>High Mids (2–4 kHz): </strong>Presence and definition; can become harsh if overboosted.</li>
//             <li><strong>Presence (4–7 kHz): </strong>Adds clarity and articulation.</li>
//             <li><strong>Brilliance (7–20 kHz):  </strong>Airiness and sparkle; excessive boosting here introduces hiss.</li>
//         </ul>
//         <p>These ranges aren’t strict boundaries but serve as guidelines for navigating EQ.</p>
//         <h2 style={{marginTop:'30px'}}>Applications in Music Production</h2>
//         <p>EQ is used across all stages of music production:</p>
//         <ul class="a">
//             <li><strong>Tracking: </strong>Cutting low-end rumble from mics, shaping instrument tone.</li>
//             <li><strong>Mixing: </strong>Carving space for each element, avoiding frequency clashes.</li>
//             <li><strong>Mastering: </strong>Making broad tonal adjustments to the final stereo mix.</li>
//         </ul>
//         <p>One powerful use of EQ is "mirrored EQ"—where complementary instruments are cut and boosted in opposing ranges to reduce masking. For example, boosting bass guitar at 80 Hz while cutting the kick drum there, and reversing the process at 100 Hz, can help both elements coexist clearly.</p>
//         <h2 style={{marginTop:'30px'}}>Live Sound and Feedback Control</h2>
//         <p>EQ is also vital in live settings, particularly for managing feedback. A narrow cut with a high-Q parametric EQ can notch out a problematic frequency without affecting the overall tone. Graphic EQs are often used to flatten the room response or tame resonant peaks in a venue.</p>
//         <p>Room tuning with a graphic EQ involves playing pink noise and analyzing the response with a spectrum analyzer. Boosts and cuts are then made to compensate for acoustic anomalies.</p>
//         <h2 style={{marginTop:'30px'}}>Corrective vs. Creative EQ</h2>
//         <p>EQ can be both a corrective tool and a creative one:</p>
//         <ul class="a">
//             <li><strong>Corrective EQ: </strong>Used to fix issues, like removing hiss, rumble, or nasal vocal resonances.</li>
//             <li><strong>Creative EQ: </strong>Used to shape the tone for aesthetic purposes—adding warmth, clarity, or sparkle.</li>
//         </ul>
//         <p>For example, a gentle high-shelf boost can add air to a vocal, while a wide cut around 300 Hz might reduce muddiness in a mix.</p>
//         <h2 style={{marginTop:'30px'}}>EQ in Practice: Tips and Techniques</h2>
//         <h3>Use in Context</h3>
//         <p>Always EQ a sound in the context of the full mix. A soloed instrument might sound thin after EQing, but in the mix it fits perfectly. EQing in isolation can lead to over-processing.</p>
//         <h3>Cut Before You Boost</h3>
//         <p>It’s often more transparent to reduce unwanted frequencies than to boost desired ones. This avoids adding unnecessary gain and reduces potential phase issues.</p>
//         <h3>Sweep and Cut</h3>
//         <p>To find unpleasant resonances, create a narrow boost with a high Q, sweep it across the spectrum, and cut where the sound is harsh or boomy.</p>
//         <h3>Low-Cut Non-Bass Elements</h3>
//         <p>Many instruments contain unnecessary low-end energy. High-pass filters can clean up pads, vocals, guitars, and FX to make room for bass and kick.</p>
//         <h3>Use Automation</h3>
//         <p>Sometimes a static EQ setting isn’t enough. Automating EQ settings allows dynamic tonal changes during a song—especially useful during transitions or builds.</p>
//         <h2 style={{marginTop:'30px'}}>Pitfalls to Avoid</h2>
//         <h3>Overuse</h3>
//         <p>Applying too much EQ can lead to unnatural, harsh, or overly thin sounds. When multiple EQ bands are in use, cumulative phase shift can degrade clarity.</p>
//         <h3>Chasing Presets</h3>
//         <p>EQ presets can serve as starting points, but every mix is different. Relying on a "Vocal Boost" preset may not suit your unique vocal recording.</p>
//         <h3>Boosting Frequencies That Aren’t There</h3>
//         <p>If a sound lacks presence, boosting high mids won’t help if the frequencies don’t exist in the recording. EQ can't add what isn’t there.</p>
//         <h3>Ignoring the Bigger Picture</h3>
//         <p>A sound may shine on its own, but if it fights other instruments in the mix, it can muddy the whole track. EQ should serve the song, not individual sounds.</p>
//         <h2 style={{marginTop:'30px'}}>Specialized EQ Uses</h2>
//         <ul class="a">
//         <li><strong>Rumble Filters: </strong>High-pass filters to remove subsonic noise from turntables or poor recordings.</li>
//         <li><strong>Notch Filters: </strong>Ultra-narrow cuts used for surgical removal of hum or feedback.</li>
//         <li><strong>Crossover EQ: </strong>Used in speaker systems to split frequencies between woofers and tweeters.</li>
//         <li><strong>De-essing: </strong>A form of dynamic EQ targeting harsh sibilant sounds around 4–8 kHz.</li>
//         </ul>
//         <h2 style={{marginTop:'30px'}}>EQ in Hardware and Software</h2>
//         <p>EQ can be found in:</p>
//         <ul class="a">
//             <li><strong>Mixing Consoles: </strong>Usually semi-parametric, often with shelving high and low bands.</li>
//             <li><strong>Hardware EQs: </strong>Analog outboard gear like Pultec-style tube EQs add warmth and coloration.</li>
//             <li><strong>Plugins: </strong>Digital EQs in DAWs offer graphical interfaces and surgical precision.</li>
//             <li><strong>Multiband EQs: </strong>Offer separate control for frequency bands, often used in mastering.</li>
//         </ul>

//         <p>Some analog EQs are prized for their musicality and phase characteristics. Pultec EQs, for example, allow simultaneous boosting and cutting at the same frequency for a unique tonal shape.</p>
//         <h2 style={{marginTop:'30px'}}>The Subtle Art of EQ</h2>
//         <p>While EQ is often thought of as a technical tool, it’s ultimately about feel and expression. Great EQ decisions come not just from charts or visual graphs, but from listening deeply to how frequencies interact and using that knowledge to bring a track to life. Whether you're chasing clarity, warmth, punch, or air, EQ remains one of the most powerful tools in the audio engineer's toolkit. Use it with intention, trust your ears, and let it serve the music.</p>







//     <br />
//     <br />
//     <br />
//         <p style={{textAlign:'center'}}>* We are not affiliated with any of the brands or artists listed on this site *</p>
      
//       </div>
//        </>

//     )
// }

